import { createContext, useContext, useMemo } from "react";
import { useChat, type ChatState } from "@/hooks/useChat";
import type { ChatMessage } from "@/lib/api";

type ChatContextValue = ChatState & {
	loadMessages: (conversationId: string, cursor?: string) => Promise<void>;
	loadMore: () => void;
	sendMessage: (content: string, replyToId?: string, attachmentIds?: string[]) => Promise<ChatMessage | null>;
	editMessage: (messageId: string, content: string) => Promise<ChatMessage | null>;
	deleteMessage: (messageId: string) => Promise<void>;
	addRealtimeMessage: (msg: ChatMessage) => void;
	markRead: (conversationId: string, lastMessageId: string) => Promise<void>;
	loadUnread: () => Promise<void>;
};

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
	const chat = useChat();
	const value = useMemo(() => chat, [chat]);
	return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChatContext(): ChatContextValue {
	const ctx = useContext(ChatContext);
	if (!ctx) throw new Error("useChatContext must be used within ChatProvider");
	return ctx;
}
